import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService } from '../services/authService';
import { User } from '../types';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    login: (user: User) => void;
    logout: () => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Function to fetch current user
    const fetchUser = useCallback(async () => {
        // Check if token exists first
        if (!authService.isAuthenticated()) {
            setUser(null);
            setLoading(false);
            return;
        }

        try {
            // Don't set loading true here to avoid flickering if we're just refreshing
            const userData = await authService.getCurrentUser();
            setUser(userData);
        } catch (error: any) {
            console.error('Error fetching user:', error);
            // Silently handle invalid token - just clear it
            if (error.response?.status === 401 || error.response?.status === 422) {
                authService.logout();
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();

        // Listen for custom auth events to sync state across tabs/components
        const onAuthChanged = () => {
            // If logging in, we might want to show loading
            // If logging out, we can just clear immediately
            if (authService.isAuthenticated()) {
                fetchUser();
            } else {
                setUser(null);
                setLoading(false);
            }
        };

        // Listen for storage events (multi-tab support)
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'access_token') {
                onAuthChanged();
            }
        };

        window.addEventListener('auth-changed', onAuthChanged);
        window.addEventListener('storage', onStorage);

        return () => {
            window.removeEventListener('auth-changed', onAuthChanged);
            window.removeEventListener('storage', onStorage);
        };
    }, [fetchUser]);

    const login = useCallback((newUser: User) => {
        setUser(newUser);
    }, []);

    const logout = useCallback(() => {
        authService.logout();
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        await fetchUser();
    }, [fetchUser]);

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated: !!user,
            login,
            logout,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Internal hook for consuming context (mostly used by the legacy useAuth hook)
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};
